import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/Buttons/Button';
import { DialogBody, DialogFooter } from 'src/components/Modals/FormDialog';
import container from 'src/container';
import { CodeStep } from './CodeStep';
import { ContactInput } from './ContactInput';
import {
    AccessSession,
    Contact,
    ContactKind,
    accessErrorMessage,
    formatContact,
} from './accessErrors';

const {
    cradle: { authService },
} = container;

type ChangeContactPanelProps = {
    kind: ContactKind;
    current?: Contact;
    /** A staff-entered contact the person is confirming. */
    unconfirmed?: Contact;
    onSaved: (contact: Contact) => Promise<void>;
    onCancel: () => void;
};

/** Sends a code to the new contact and saves it once confirmed; the old one keeps working until then. */
export const ChangeContactPanel = ({
    kind,
    current,
    unconfirmed,
    onSaved,
    onCancel,
}: ChangeContactPanelProps) => {
    const { t } = useTranslation();
    const [contact, setContact] = useState<Contact>(
        unconfirmed ?? { kind, value: '' },
    );
    const [session, setSession] = useState<AccessSession>();
    const [error, setError] = useState<string>();
    const [busy, setBusy] = useState(false);

    const sendCode = async (event: FormEvent) => {
        event.preventDefault();
        setBusy(true);
        setError(undefined);
        try {
            setSession(
                await authService.accountAccess<AccessSession>(
                    'contacts/start',
                    contact,
                    true,
                ),
            );
        } catch (cause) {
            setError(accessErrorMessage(cause, t));
        } finally {
            setBusy(false);
        }
    };

    const confirm = async (code: string) => {
        if (!session) return;
        await authService.accountAccess(
            'contacts/finish',
            { session: session.session, code },
            true,
        );
        await onSaved(session.contact);
    };

    const description = unconfirmed
        ? t('accountAccess.contacts.confirmNote')
        : current
        ? t('accountAccess.contacts.keepsWorking', {
              contact: formatContact(current),
          })
        : undefined;

    if (session)
        return (
            <CodeStep
                session={session}
                settings
                submitLabel={t('accountAccess.contacts.confirm')}
                onSubmit={confirm}
                onCancel={onCancel}
            />
        );

    return (
        <form className="flex min-h-0 flex-1 flex-col" onSubmit={sendCode}>
            <DialogBody>
                {description && (
                    <p className="text-sm text-mongooseGray">{description}</p>
                )}
                <ContactInput
                    lockedKind
                    contact={contact}
                    onChange={setContact}
                    disabled={busy}
                    error={error}
                    label={t(
                        unconfirmed
                            ? kind === 'phone'
                                ? 'accountAccess.phoneLabel'
                                : 'accountAccess.emailLabel'
                            : kind === 'phone'
                            ? 'accountAccess.contacts.newPhone'
                            : 'accountAccess.contacts.newEmail',
                    )}
                />
            </DialogBody>
            <DialogFooter>
                <Button
                    title={t('common.cancel')}
                    onClick={onCancel}
                    size="comfortable"
                />
                <Button
                    submit
                    active
                    size="comfortable"
                    title={t('accountAccess.sendCode')}
                    disabled={busy || !contact.value.trim()}
                />
            </DialogFooter>
        </form>
    );
};
